import { createContext, useContext, ReactNode, useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import { Language } from '@/lib/translations';

interface RestaurantContextType {
  currencyCode?: string;
  defaultLanguage?: string;
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

interface RestaurantProviderProps {
  children: ReactNode;
  currencyCode?: string;
  defaultLanguage?: string;
}

export function RestaurantProvider({ children, currencyCode, defaultLanguage }: RestaurantProviderProps) {
  const { setCurrencyCode, setLanguage, language } = useLanguage();

  // Automatically sync currency to LanguageProvider when currencyCode is available
  useEffect(() => {
    if (currencyCode) {
      setCurrencyCode(currencyCode);
    }

    // Cleanup: clear currency code on unmount (don't force USD)
    return () => {
      setCurrencyCode(undefined);
    };
  }, [currencyCode, setCurrencyCode]);

  useEffect(() => {
    // Only set the language if defaultLanguage is provided and differs from the current language,
    // and if there's no user-preference already stored in localStorage.
    const userStoredLanguage = localStorage.getItem('language');
    if (defaultLanguage && defaultLanguage !== language && !userStoredLanguage) {
      setLanguage(defaultLanguage as Language);
    }
  }, [defaultLanguage, language, setLanguage]);

  return (
    <RestaurantContext.Provider value={{ currencyCode, defaultLanguage }}>
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurant() {
  const context = useContext(RestaurantContext);
  return context || {};
}
